import { useMemo } from "react";
import { Paper, Title, Box, Text } from "@mantine/core";
import { BarChart } from "@mantine/charts";

/**
 * When during the day the site is busy, in UTC.
 *
 * Deliberately UTC and labelled as such. This used to bucket by the VIEWER's local hour, so the
 * same brand's "quietest hour" moved depending on who opened the page — and the answer silently
 * disagreed with every other figure in the product, all of which are UTC.
 */
export default function ActiveTimes({ byHour, isLoading }) {
  const data = useMemo(() => {
    // Zero-fill all 24 hours: a missing hour is a genuine zero, and a gap in the axis would
    // misrepresent a quiet night as a narrower day.
    const counts = new Map((byHour ?? []).map((h) => [h.hour, h.events]));
    return Array.from({ length: 24 }, (_, hour) => ({
      hour: `${String(hour).padStart(2, "0")}:00`,
      events: counts.get(hour) ?? 0,
    }));
  }, [byHour]);

  const hasData = data.some((d) => d.events > 0);

  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        <Title order={4}>Active Times</Title>
        <Box h={250} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Text c="dimmed">Loading...</Text>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Title order={4} mb={4}>Active Times</Title>
      <Text size="xs" c="dimmed" mb="md">Hour of day, UTC</Text>
      <Box h={250}>
        {hasData ? (
          <BarChart
            h={250}
            data={data}
            dataKey="hour"
            series={[{ name: "events", label: "Events", color: "indigo.6" }]}
            tickLine="x"
          />
        ) : (
          <Box h="100%" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Text c="dimmed">No data available</Text>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
